import Carousel from 'components/Carousel';
import DiscountBubble from 'components/DiscountBubble';
import Img from 'components/Img';
import { LayoutContainer } from 'components/Layout';
import {
	ScArrowLink,
	ScLink,
	type ValidSitecoreLink,
} from 'components/SitecoreLink';
import Text from 'components/Text';
import type { Field, HeadingLevel } from 'models/sitecore';

interface ItemProps {
	campaignBubbleLabel?: string;
	campaignBubbleSale?: boolean;
	campaignBubbleText?: string;
	heading?: Field<string>;
	imageAlt?: string;
	imageLoading?: 'eager' | 'lazy';
	imageSrc: string;
	link: ValidSitecoreLink;
}

function Item({
	campaignBubbleLabel,
	campaignBubbleSale,
	campaignBubbleText,
	heading,
	imageAlt,
	imageLoading,
	imageSrc,
	link,
}: ItemProps) {
	return (
		<>
			<ScLink
				link={link}
				isScreenReaderHidden={!imageAlt && !campaignBubbleText}
				// Relative for discount bubble positioning.
				className="relative block"
			>
				<Img
					src={imageSrc}
					alt={imageAlt}
					width={393}
					height={393}
					loading={imageLoading}
					className="w-full sm:object-cover"
				/>
				{campaignBubbleText && (
					<>
						<DiscountBubble
							label={campaignBubbleLabel}
							text={campaignBubbleText}
							isSale={campaignBubbleSale}
							isPositioned
							size="small"
							className="lg:hidden"
						/>
						<DiscountBubble
							label={campaignBubbleLabel}
							text={campaignBubbleText}
							isSale={campaignBubbleSale}
							isPositioned
							size="default"
							className="max-lg:hidden"
						/>
					</>
				)}
			</ScLink>
			{heading?.value && <Text as="h2" field={heading} className="mt-r-sm" />}
			<ScArrowLink link={link} className="mt-r-sm" />
		</>
	);
}
Item.displayName = 'TeaserCarousel_Item';

interface Props {
	className?: string;
	heading?: Field<string>;
	headingLevel?: HeadingLevel;
	id?: string;
	items: ItemProps[];
}

export default function TeaserCarousel({
	className,
	heading,
	headingLevel = 'h2',
	id,
	items,
}: Props) {
	return (
		<LayoutContainer id={id} outerClassName={className}>
			<Text as={headingLevel} field={heading} className="mb-8" />
			<Carousel
				carouselClassName="auto-cols-[66%] gap-x-r-md sm:auto-cols-[calc(33.333%-(var(--r-space-md)*0.666))] md:auto-cols-[calc(25%-(var(--r-space-md)*0.75))]"
				gutter="max-sm:md"
				items={items.map((item, i) => (
					<Item
						key={item.link.text}
						imageLoading={i === 0 || i === 1 ? 'eager' : 'lazy'}
						{...item}
					/>
				))}
			/>
		</LayoutContainer>
	);
}
TeaserCarousel.displayName = 'TeaserCarousel';
